import { useEffect, useState, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, BACKEND_URL } from "@/lib/api";
import { useCompany } from "@/lib/company";
import { useAuth } from "@/lib/auth";
import { emitAction, useActionListener } from "@/lib/createBus";
import { TID } from "@/constants/testIds";
import { BUSINESS_TYPES } from "@/constants/businessTypes";
import { CheckCircle2, ChevronRight, Loader2, Sparkles, ArrowLeft, Upload } from "lucide-react";
import { toast } from "sonner";
import { useColumnBox } from "@/hooks/useColumnBox";
import ResponsibilitiesChecklist from "@/components/ResponsibilitiesChecklist";
import PlaidLinkButton from "@/components/PlaidLinkButton";
import { IndustryTemplatePicker } from "@/components/AIFirstControls";
import WelcomeModal, { hasSeenWelcome, markWelcomeSeen } from "@/components/WelcomeModal";
import { IndustrySelect } from "@/components/IndustrySelect";
import StatementsTab from "@/components/StatementsTab";
import InlineQboConnect from "@/components/InlineQboConnect";
import { institutionLogoUrl } from "@/lib/institutionLogo";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

// AI onboarding coach — greetings posted into the chat on each step to make
// the flow feel like a live accountant is walking you through. Each entry
// is fired once per step per session. When the coach knows how to extract
// structured fields from the user's freeform reply, `extractStep` is set
// and the frontend calls /onboarding/extract-step to auto-fill the form
// and (optionally) auto-advance to the next step.
const COACH_SCRIPTS = {
  1: {
    key: "onboarding.contact",
    message: (ctx) =>
      `Who should I contact if I have questions about a transaction on ${ctx.name || "these books"} down the road? Give me a name, email and phone number below — and add anyone else worth looping in.`,
    // No extractStep — user drives the form.
  },
  2: {
    key: "onboarding.business_type",
    message: (ctx) => {
      const bt = ctx.current?.business_type;
      if (bt) {
        return `I have ${ctx.name || "this business"} down as **${bt}**. Still right, or should we change it? Pick the closest match below.`;
      }
      return `What kind of entity is ${ctx.name || "this business"}? Sole prop, LLC, S-corp, C-corp? I'll pick the right chart of accounts once you tell me.`;
    },
    extractStep: "business_type_pick",
    ready: (fields, answers) => Boolean(fields.business_type || answers.business_type),
    confirm: (bits) => `Got it — ${bits}. Moving on…`,
  },
  3: {
    key: "onboarding.business_profile",
    message: (ctx) =>
      `What industry does ${ctx.name || "the business"} work in? Take a look at the industries in the dropdown and pick the one that best fits.`,
    extractStep: "business_profile",
    // Auto-advance when we already had (or just captured) both essentials,
    // OR when the user tells us they're happy with what's there.
    ready: (fields, answers) => {
      const haveBoth =
        (fields.business_type || answers.business_type) &&
        (fields.business_description || answers.business_description);
      return haveBoth && (fields.confirm_move_on || Object.keys(fields).length > 0);
    },
    confirm: (bits, ready, fields) => {
      if (fields.confirm_move_on && Object.keys(fields).length === 1) {
        return `Great — moving on…`;
      }
      return ready
        ? `Got it — filled in ${bits}. Moving to the next step in a moment…`
        : `Got it — filled in ${bits}. Anything else to add?`;
    },
  },
  4: {
    key: "onboarding.qbo_link",
    message: () =>
      `Do you already use QuickBooks Online and want to migrate the information?`,
    extractStep: "qbo_link",
    ready: (fields) => fields.qbo === "yes" || fields.qbo === "no",
    confirm: (_bits, _ready, fields) =>
      fields.qbo === "yes"
        ? `Perfect — click "Connect to QuickBooks Online" below to link your account. I'll wait here while you go through the QBO consent screen.`
        : `Got it — we'll set up fresh together. Moving on…`,
  },
  5: {
    key: "onboarding.interview",
    message: () =>
      `Five short questions coming up — should take about 30 seconds. Your answers help me tailor the chart of accounts and pre-seed bank-feed rules for your exact business. Hit "Start AI interview" whenever you're ready.`,
    // No extractStep — user drives the interview UI, not chat.
  },
  6: {
    key: "onboarding.coa",
    message: () =>
      `Time for your Chart of Accounts. I've got a GAAP baseline; hit "Suggest tailored accounts" and I'll propose 15-25 industry-specific ones you can review. If you want anything specific (e.g. "add a food-truck fuel account", "we don't need consulting revenue"), just tell me and I'll factor it in.`,
    extractStep: "coa_overrides",
    // Never auto-advance — user has to actually generate + apply the CoA.
    ready: () => false,
    confirm: (bits) => `Noted — ${bits}. I'll factor that in when generating your CoA.`,
  },
  7: {
    key: "onboarding.plaid",
    message: () =>
      `We are on a roll! Do you want to hook up your bank accounts so that we can download transactions automatically?`,
    extractStep: "plaid_intent",
    // Advance on either explicit "skip"/"no" OR after they successfully
    // linked at least one Plaid account (handled by the local move-on
    // regex catching "yes" once accounts are visible).
    ready: (fields) => fields.skip === true,
    confirm: (_bits, _ready, fields) =>
      fields.skip
        ? `No problem — we'll skip Plaid for now. You can connect banks later from Settings. Moving on…`
        : `Got it — launch Plaid whenever you're ready.`,
  },
  8: {
    key: "onboarding.plaid_credit",
    message: () =>
      `Any credit cards to add? Same Plaid flow — we'll pull statements automatically and I'll categorize each charge for you. Or say "skip" if you don't have any business cards.`,
    extractStep: "plaid_credit_intent",
    ready: (fields) => fields.skip === true,
    confirm: (_bits, _ready, fields) =>
      fields.skip
        ? `No problem — we'll skip credit cards for now. You can connect them later from Settings. Moving on…`
        : `Got it — launch Plaid whenever you're ready to link a card.`,
  },
  9: {
    key: "onboarding.veryfi",
    message: () =>
      `Any statements Plaid couldn't reach? Old paper statements, credit-union PDFs, receipts — drop them here and Veryfi OCR will pull the transactions and I'll categorize each. Or say "skip" if you don't have any.`,
    extractStep: "veryfi_intent",
    ready: (fields) => fields.skip === true,
    confirm: (_bits, _ready, fields) =>
      fields.skip
        ? `Skipping statement uploads. Moving on…`
        : `Got it — upload whenever ready.`,
  },
  10: {
    key: "onboarding.responsibilities",
    message: () =>
      `Last practical bit: who does what each month? For each recurring activity below, tell me who owns it — "Client", "Accountant", or "Both". You can change any of this later from the To Do page.`,
    // No extractStep — user drives the checklist UI.
  },
  11: {
    key: "onboarding.ready",
    message: () =>
      `Company setup is all done — nice work! I'm working in the background now, handling the accounting drudgery. Give me a couple of minutes to finish importing and categorizing transactions. Go ahead and hit "Next step" whenever you're ready — I'll see you there.`,
    extractStep: "ready_confirm",
    ready: (fields) => fields.confirm === true,
    confirm: () => `Perfect — taking you in now.`,
  },
};

const STEPS = [
  "Starting",
  "Contact",
  "Business type",
  "Business profile",
  "QuickBooks link",
  "AI Interview",
  "AI Chart of Accounts",
  "Bank connection (Plaid)",
  "Credit card connection (Plaid)",
  "Statement upload (Veryfi)",
  "Responsibilities",
  "Ready to review",
];

function OnboardingContacts({ answers, setAnswers, persist, userEmail }) {
  const contacts = Array.isArray(answers.contacts) && answers.contacts.length > 0
    ? answers.contacts
    : [{ name: "", email: "", phone: "", send_invite: false }];

  const commit = (next) => {
    const nextAns = { ...answers, contacts: next };
    setAnswers(nextAns);
    persist({ answers: nextAns });
  };

  const updateAt = (idx, patch) => {
    const next = contacts.map((c, i) => (i === idx ? { ...c, ...patch } : c));
    commit(next);
  };

  const addContact = () => {
    commit([...contacts, { name: "", email: "", phone: "", send_invite: false }]);
  };

  const removeAt = (idx) => {
    if (contacts.length <= 1) {
      commit([{ name: "", email: "", phone: "", send_invite: false }]);
      return;
    }
    commit(contacts.filter((_, i) => i !== idx));
  };

  const normalize = (e) => (e || "").trim().toLowerCase();
  const loggedInEmailLc = normalize(userEmail);

  return (
    <div className="space-y-4" data-testid="onboarding-contacts">
      <div>
        <h2 className="font-heading text-xl font-semibold">Contact</h2>
        <p className="text-sm text-slate-500 mt-0.5">
          In the future if I have questions about transactions or I notice a
          problem, who should I contact?
        </p>
      </div>

      <div className="space-y-4">
        {contacts.map((c, idx) => {
          const emailLc = normalize(c.email);
          const showInvite = emailLc && emailLc !== loggedInEmailLc;
          return (
            <div
              key={idx}
              className="rounded-lg border border-slate-200 bg-white p-4 space-y-3"
              data-testid={`onboarding-contact-row-${idx}`}
            >
              <div className="flex items-center justify-between">
                <span className="text-xs uppercase text-slate-500 tracking-wide font-semibold">
                  {idx === 0 ? "Primary contact" : `Contact ${idx + 1}`}
                </span>
                {contacts.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeAt(idx)}
                    data-testid={`onboarding-contact-remove-${idx}`}
                    className="text-xs text-slate-400 hover:text-red-600"
                  >
                    Remove
                  </button>
                )}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="text-xs uppercase text-slate-500 tracking-wide">Name</label>
                  <input
                    type="text"
                    value={c.name || ""}
                    onChange={(e) => updateAt(idx, { name: e.target.value })}
                    data-testid={`onboarding-contact-name-${idx}`}
                    className="w-full mt-1 border border-slate-300 rounded-md px-3 py-2 text-sm focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 focus:outline-none"
                    placeholder="e.g. Sarah Kim"
                  />
                </div>
                <div>
                  <label className="text-xs uppercase text-slate-500 tracking-wide">Email</label>
                  <input
                    type="email"
                    value={c.email || ""}
                    onChange={(e) => updateAt(idx, { email: e.target.value })}
                    data-testid={`onboarding-contact-email-${idx}`}
                    className="w-full mt-1 border border-slate-300 rounded-md px-3 py-2 text-sm focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 focus:outline-none"
                    placeholder="name@company.com"
                  />
                </div>
                <div className="sm:col-span-2">
                  <label className="text-xs uppercase text-slate-500 tracking-wide">Phone</label>
                  <input
                    type="tel"
                    value={c.phone || ""}
                    onChange={(e) => updateAt(idx, { phone: e.target.value })}
                    data-testid={`onboarding-contact-phone-${idx}`}
                    className="w-full mt-1 border border-slate-300 rounded-md px-3 py-2 text-sm focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 focus:outline-none"
                    placeholder="+1 (555) 123-4567"
                  />
                </div>
              </div>

              {showInvite && (
                <label
                  className="flex items-center gap-2 pt-1 cursor-pointer select-none"
                  data-testid={`onboarding-contact-invite-label-${idx}`}
                >
                  <input
                    type="checkbox"
                    checked={!!c.send_invite}
                    onChange={(e) => updateAt(idx, { send_invite: e.target.checked })}
                    data-testid={`onboarding-contact-invite-${idx}`}
                    className="rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                  />
                  <span className="text-sm text-slate-700">
                    Send an invite so this person can log in to the books too
                  </span>
                </label>
              )}
            </div>
          );
        })}
      </div>

      <button
        type="button"
        onClick={addContact}
        data-testid="onboarding-contact-add"
        className="inline-flex items-center gap-1.5 text-sm text-indigo-600 hover:text-indigo-800 font-medium"
      >
        + Add another contact
      </button>
    </div>
  );
}

function AiOnboardingPrefs({ answers, setAnswers, persist }) {
  const aiAssist = answers.ai_assist; // "yes" | "no" | undefined
  const aiAudio = answers.ai_audio;   // "yes" | "no" | undefined

  const pickAssist = (v) => {
    const nextAns = v === "yes"
      ? { ...answers, ai_assist: v }
      // Muting AI also clears the audio sub-answer so the follow-up
      // question resets its picked state next time the user opts back in.
      : { ...answers, ai_assist: v, ai_audio: undefined };
    setAnswers(nextAns);
    persist({ answers: nextAns });
    if (v === "yes") {
      emitAction("ai-open");
    } else {
      emitAction("ai-close");
      // If the user turned off AI entirely, also mute any lingering TTS.
      try {
        localStorage.setItem("axiom_tts", "0");
        window.dispatchEvent(new CustomEvent("axiom-tts-changed", { detail: { on: false } }));
      } catch { /* localStorage disabled — silently ignore */ }
    }
  };

  const pickAudio = (v) => {
    const nextAns = { ...answers, ai_audio: v };
    setAnswers(nextAns);
    persist({ answers: nextAns });
    const on = v === "yes";
    try {
      localStorage.setItem("axiom_tts", on ? "1" : "0");
      window.dispatchEvent(new CustomEvent("axiom-tts-changed", { detail: { on } }));
    } catch { /* localStorage disabled — silently ignore */ }
  };

  const YesNo = ({ value, onPick, testidPrefix }) => (
    <div className="mt-2 flex gap-2" data-testid={`${testidPrefix}-picker`}>
      {[
        { v: "yes", label: "Yes" },
        { v: "no",  label: "No"  },
      ].map(({ v, label }) => {
        const selected = value === v;
        return (
          <button
            key={v}
            type="button"
            role="radio"
            aria-checked={selected}
            data-testid={`${testidPrefix}-${v}`}
            onClick={() => onPick(v)}
            className={`flex items-center gap-2 rounded-lg border px-4 py-2 text-sm transition
              ${selected
                ? "border-indigo-500 bg-indigo-50 ring-2 ring-indigo-100 text-indigo-900 font-medium"
                : "border-slate-200 bg-white text-slate-700 hover:border-slate-300 hover:bg-slate-50"}`}
          >
            <span
              className={`w-4 h-4 rounded-full border-2 flex items-center justify-center
                ${selected ? "border-indigo-500" : "border-slate-300"}`}
            >
              {selected && <span className="w-2 h-2 rounded-full bg-indigo-500" />}
            </span>
            {label}
          </button>
        );
      })}
    </div>
  );

  return (
    <div className="space-y-4" data-testid="onboarding-ai-prefs">
      <div>
        <label className="text-sm font-medium text-slate-900">
          Do you want AI to assist you with the onboarding process?
        </label>
        <YesNo
          value={aiAssist}
          onPick={pickAssist}
          testidPrefix="onboarding-ai-assist"
        />
      </div>

      {aiAssist === "yes" && (
        <div>
          <label className="text-sm font-medium text-slate-900">
            Do you want to turn on the AI audio so that you can hear the AI?
          </label>
          <YesNo
            value={aiAudio}
            onPick={pickAudio}
            testidPrefix="onboarding-ai-audio"
          />
        </div>
      )}
    </div>
  );
}

export default function Onboarding() {
  const nav = useNavigate();
  const { currentId, current, refresh } = useCompany();
  const { user } = useAuth();
  const [step, setStep] = useState(0);
  // First visit to onboarding → play the welcome tour once per user
  // (same localStorage flag the dashboard uses, so it never double-fires).
  const [welcomeOpen, setWelcomeOpen] = useState(false);
  useEffect(() => {
    if (!user?.id || user?.is_demo_visitor || hasSeenWelcome(user.id)) return;
    const t = setTimeout(() => setWelcomeOpen(true), 400);
    return () => clearTimeout(t);
  }, [user?.id, user?.is_demo_visitor]);
  const closeWelcome = () => {
    setWelcomeOpen(false);
    if (user?.id) markWelcomeSeen(user.id);
  };
  const [answers, setAnswers] = useState({});
  const [busy, setBusy] = useState(false);
  // Disables the "Finish" / "Next step" button on the last step while
  // finish() awaits its persist call — invite emails fire in the
  // background so they don't hold up navigation.
  const [finishing, setFinishing] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [coaSelected, setCoaSelected] = useState(new Set());
  const [previewing, setPreviewing] = useState(false);
  const [adding, setAdding] = useState(false);
  const [addedCount, setAddedCount] = useState(0);
  // AI Interview
  const [interviewQs, setInterviewQs] = useState([]);
  const [interviewAns, setInterviewAns] = useState({}); // {id: answer}
  const [interviewLoading, setInterviewLoading] = useState(false);
  const [interviewApplying, setInterviewApplying] = useState(false);
  const [interviewResult, setInterviewResult] = useState(null); // {accounts, rules, inserted_accounts, inserted_rules, rules_applied_to_transactions}
  const [plaidAccts, setPlaidAccts] = useState([]);
  const [selectedPlaid, setSelectedPlaid] = useState(new Set());
  // Hydrate previously-linked Plaid accounts (both freshly-linked ones and
  // those already imported into the ledger) whenever the company changes
  // OR the user navigates to the Plaid step. Persists across refresh
  // until onboarding is complete.
  useEffect(() => {
    if (!currentId || (step !== 7 && step !== 8)) return;
    api.get(`/companies/${currentId}/onboarding/plaid/items`).then(r => {
      const rows = r.data?.accounts || [];
      if (!rows.length) return;
      const mapped = rows.map(a => ({
        id: a.account_id,
        name: `${a.name} ...${a.mask || ""}`,
        institution: a.institution_name,
        subtype: a.subtype || a.type,
        balance: a.balance_current || 0,
        alreadyImported: a.imported,
      }));
      setPlaidAccts(prev => {
        const known = new Set(prev.map(x => x.id));
        // Prefer already-loaded rows (fresh-link data) over refetched ones,
        // so an in-progress toggle from onPlaidLinked isn't clobbered.
        return [...prev, ...mapped.filter(a => !known.has(a.id))];
      });
      // Mark already-imported accounts as auto-imported so the "Import"
      // button correctly dims for them.
      const imported = mapped.filter(a => a.alreadyImported).map(a => a.id);
      imported.forEach(id => autoImportedRef.current.add(id));
      // Auto-check anything not-yet-imported so the user's default is
      // "connect these too". Already-imported accounts stay unchecked.
      setSelectedPlaid(prev => {
        const next = new Set(prev);
        mapped.filter(a => !a.alreadyImported).forEach(a => next.add(a.id));
        return next;
      });
    }).catch(() => { /* first-time users have no items yet — silent */ });
  }, [currentId, step]);
  const [imported, setImported] = useState({ plaid: 0, veryfi: 0 });
  // Guard state for the "you haven't imported yet" popup on step 7 (bank)
  // and step 8 (credit cards). Holds { targetStep, kind, count } while the
  // AlertDialog is open, then cleared once the user picks a branch.
  const [pendingImportSkip, setPendingImportSkip] = useState(null);
  // Set of plaid_account_ids we've already auto-imported this session.
  // Guards against a re-mount or a second linking re-triggering the whole
  // "Nice — I linked X accounts…" flow for accounts already in the ledger.
  const autoImportedRef = useRef(new Set());
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  // Guards the "greet on step change" effect from firing with the default
  // step=0 while onboarding state is still being fetched from the server.
  const [loaded, setLoaded] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  // Track whether we've processed the ?qbo=connected return-from-Intuit
  // handoff. Runs AFTER the onboarding-state load resolves so we can
  // merge with the persisted answers instead of racing against them.
  const qboReturnHandled = useRef(false);

  // Toast the return-from-Intuit outcome, then strip the query
  // params so a refresh doesn't repeat. State (answers.qbo="yes",
  // step=1) is baked in by the load effect above, so we don't touch
  // it here. The URL-strip via `setSearchParams` is what prevents
  // this effect from double-firing — no ref-guard needed.
  useEffect(() => {
    if (!loaded) return;
    const qbo = searchParams.get("qbo");
    const qboErr = searchParams.get("qbo_error");
    if (!qbo && !qboErr) return;
    if (qbo === "connected") {
      toast.success("QuickBooks connected — preview + migrate your books below");
    } else if (qboErr) {
      toast.error(`QuickBooks connection failed: ${qboErr}`, { duration: 15000 });
    }
    const next = new URLSearchParams(searchParams);
    next.delete("qbo");
    next.delete("qbo_error");
    next.delete("realm");
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loaded]);

  useEffect(() => {
    if (!currentId) return;
    api.get(`/companies/${currentId}/onboarding`).then(r => {
      let step0 = r.data.onboarding.step || 0;
      let ans = r.data.onboarding.answers || {};
      // If the persisted step is an AI-only step (2 or 3) but the mode is
      // Simple (the new default), hop forward past every AI-only step so
      // the user doesn't land on a page they can't see.
      const modeNow = ans.onboarding_mode === "guided" ? "guided" : "simple";
      if (modeNow === "simple") {
        while (AI_ONLY_STEPS.has(step0) && step0 < STEPS.length) step0 += 1;
      }
      // Return-from-Intuit shortcut. When the QBO OAuth callback lands
      // us here with `?qbo=connected`, we KNOW the user picked "Yes"
      // and successfully connected — bake that into the initial state
      // instead of racing against a separate effect that gets clobbered
      // by this very setAnswers below. Same treatment for the error
      // path so the panel still opens and shows the failure.
      const urlQbo = new URLSearchParams(window.location.search).get("qbo");
      const urlQboErr = new URLSearchParams(window.location.search).get("qbo_error");
      // If EITHER (a) the URL currently carries the return-from-Intuit
      // flag OR (b) we already handled it this session (ref true) —
      // bake qbo="yes" into the loaded state. The ref-guard survives
      // load-effect refires (which happen when `currentId` briefly
      // toggles at startup) that would otherwise clobber our stamp.
      // We still persist to the backend once so subsequent full-page
      // loads return the correct state without any refs at all.
      if (urlQbo === "connected" || urlQboErr) {
        qboReturnHandled.current = true;
      }
      if (qboReturnHandled.current) {
        ans = { ...ans, qbo: "yes" };
        step0 = 1;
      }
      if (urlQbo === "connected" || urlQboErr) {
        api.patch(`/companies/${currentId}/onboarding`, {
          answers: { ...ans },
          step: 1,
        }).catch(() => { /* non-fatal — panel state driven by conn status too */ });
      }
      setStep(step0);
      setAnswers(ans);
      setLoaded(true);
    });
  }, [currentId]);

  // AI onboarding coach — fire a greeting whenever the step changes (and
  // once on initial load). Debounced via a ref so we only greet each step
  // once per session, even if React re-renders. When the user manually
  // clicks Next / Back fast (faster than the AI can speak the current
  // step's message), the cleanup cancels the pending greeting emit AND
  // stops any in-flight speech so we don't finish reading the OLD step
  // while the user is already on a new one.
  const coachedStepsRef = useRef(new Set());
  const coachTimerRef = useRef(null);
  useEffect(() => {
    if (!currentId || !loaded) return;
    // Cancel prior speech + pending emit — the user has moved on.
    if (coachTimerRef.current) {
      clearTimeout(coachTimerRef.current);
      coachTimerRef.current = null;
    }
    try {
      if ("speechSynthesis" in window) window.speechSynthesis.cancel();
      // AiPanel exposes a hook to stop its own TTS state flags; if unavailable
      // the raw cancel() above is enough to silence the audio.
      emitAction("ai-stop-tts");
    } catch { /* noop */ }
    // Per-step coach greeting only fires when the owner explicitly opted
    // in on the Starting step (`answers.ai_assist === "yes"`). If they
    // said "no" — or haven't answered yet — we stay silent.
    if (answers?.ai_assist !== "yes") return;
    const script = COACH_SCRIPTS[step];
    if (!script) return;
    const key = `${currentId}::${script.key}`;
    if (coachedStepsRef.current.has(key)) return;
    coachedStepsRef.current.add(key);
    // Open the AI panel and post the greeting. AiPanel treats
    // `onboarding-coach-greet` like a scripted assistant message — it will
    // wear the rainbow-outline bubble because the user hasn't replied yet.
    // Delay the emit so AiPanel has time to mount its listener; otherwise
    // TTS speaks it but the chat bubble never lands.
    emitAction("ai-open");
    const userFirst = (user?.name || "").split(" ")[0];
    coachTimerRef.current = setTimeout(() => {
      coachTimerRef.current = null;
      emitAction("onboarding-coach-greet", {
        message: script.message({
          name: current?.name,
          current,
          answers,
          userFirst,
        }),
      });
    }, 500);
    // Cleanup: if the step changes again before the timer fires, this
    // cancels the queued emit so the outdated greeting never lands.
    return () => {
      if (coachTimerRef.current) {
        clearTimeout(coachTimerRef.current);
        coachTimerRef.current = null;
      }
    };
  }, [currentId, step, current?.name, loaded, answers?.ai_assist]);

  // When the user replies in the chat while on this page, feed the reply
  // through the current step's extractor and apply the returned fields.
  //
  // `useActionListener` in createBus.js binds its handler ONCE per mount
  // (empty deps), so a naive closure would freeze `step`/`answers` at
  // their initial values. Route through refs so the handler always sees
  // the latest state.
  const stepRef = useRef(step);
  const answersRef = useRef(answers);
  const nextRef = useRef(() => {});
  const finishRef = useRef(() => {});
  const plaidAcctsRef = useRef([]);
  const currentRef = useRef(current);
  // Guards against double-advance when the user fires several move-on
  // phrases in quick succession ("looks good"…"next"…"good to go"). We
  // set this the moment we schedule a next()/finish() and clear it a few
  // ticks after the step actually changes (or a 3s fail-safe).
  const advancingRef = useRef(false);
  useEffect(() => { stepRef.current = step; advancingRef.current = false; }, [step]);
  useEffect(() => { answersRef.current = answers; }, [answers]);
  useEffect(() => { plaidAcctsRef.current = plaidAccts; }, [plaidAccts]);
  useEffect(() => { currentRef.current = current; }, [current]);
  // Coach Q&A fallback — when the user's message doesn't match any local
  // shortcut and the field extractor comes up empty, hand it to the LLM
  // with step-aware context so the coach can actually answer questions
  // like "what will connecting the bank do?" or "what if I don't have any
  // statements?" instead of sitting silent.
  //
  // Wrapped in a ref because `useActionListener` binds its handler once
  // (empty deps) — a plain fn reference would be captured before
  // `currentId` resolved.
  const coachAnswerRef = useRef(async () => {});
  useEffect(() => {
    coachAnswerRef.current = async (msg, extractStep) => {
      if (!currentId) return;
      try {
        const r = await api.post(
          `/companies/${currentId}/onboarding/coach-answer`,
          { step: extractStep, message: msg },
        );
        const text = (r.data?.answer || "").trim();
        const action = r.data?.action || null;
        if (text) emitAction("onboarding-coach-greet", { message: text });
        // Server can also tell us to launch a connect flow or advance the
        // step based on the user's phrasing — honour that.
        if (action === "advance") {
          scheduleAdvanceRef.current(() => nextRef.current());
        } else if (action === "launch:plaid") {
          setTimeout(() => emitAction("plaid-launch"), 600);
        } else if (action === "launch:upload") {
          // Trigger the on-page upload button by dispatching a click via a
          // known data-testid (kept selector-free here — the button's own
          // click handler owns the flow).
          setTimeout(() => {
            const btn = document.querySelector('[data-testid="onboarding-veryfi-upload"], [data-testid="onboarding-mock-veryfi"]');
            btn?.click();
          }, 600);
        }
      } catch { /* non-fatal — silent fallback is better than a stack trace */ }
    };
  }, [currentId]);
  // Expose scheduleAdvance via ref so coachAnswer can call it without
  // hoisting issues.
  const scheduleAdvanceRef = useRef(() => {});

  useActionListener("onboarding-user-message", async (payload) => {
    // Double-advance guard: if we JUST scheduled a next()/finish(), swallow
    // subsequent messages until the step actually changes. Users often say
    // "looks good"…"next"…"good to go" in a burst and we don't want each to
    // trigger its own advance.
    if (advancingRef.current) return;
    const currentStep = stepRef.current;
    const currentAnswers = answersRef.current;
    const currentCompany = currentRef.current;
    const script = COACH_SCRIPTS[currentStep];
    if (!script?.extractStep || !currentId) return;
    const msg = (payload?.text || "").trim();
    if (!msg) return;

    const scheduleAdvance = (fn, delayMs = 1200) => {
      advancingRef.current = true;
      setTimeout(fn, delayMs);
      // Safety-net: if the step somehow doesn't change within 3s, clear the
      // lock so the coach stays responsive.
      setTimeout(() => { advancingRef.current = false; }, 3000);
    };
    scheduleAdvanceRef.current = scheduleAdvance;

    // Broad "the user is fine, move on" detector — matches when ANY of a
    // rich set of natural affirmations appears in the message, not just as
    // a whole-string anchor. Handles filler words like "no that looks good"
    // or "yeah I think this looks good actually".
    const MOVE_ON_ANY_RE = /\b(?:looks (?:good|right|fine|great|correct)|(?:that'?s|this'?s|it'?s) (?:right|fine|correct|good|perfect|great)|good to go|all (?:good|set|done)|sounds (?:good|right|fine|great)|no (?:changes?|edits?|updates?|tweaks?)|nothing to (?:change|edit|update|tweak|add)|move on|next(?: step)?|proceed|continue|keep going|(?:let'?s )?carry on|(?:that|this) works|perfect|great|correct)\b/i;
    // Whole-string minimal move-on tokens ("yes", "yep", "sure", "ok",
    // "good", "done", "nope", "no", "skip") — used for steps where a bare
    // yes/no is enough.
    const MOVE_ON_SHORT_RE = /^(?:nope?|no(?:pe)?|yes|yeah|yep|yup|sure|ok(?:ay)?|good|great|done|next|proceed|continue|skip|move on)[\s.!,?]*$/i;

    const isMoveOn = (m) => MOVE_ON_ANY_RE.test(m) || MOVE_ON_SHORT_RE.test(m);
    // "Actually…" / "no wait" / "change…" style edits — user wants to
    // modify what's already there rather than move on. Block a move-on
    // interpretation when these appear.
    const WANTS_EDIT_RE = /\b(?:actually|wait|change|edit|update|tweak|fix|correct(?: this| it| that)?(?= to| into| please)|add(?:ing)? |remove|drop|instead|different|rename|it'?s (?:actually|not)|not (?:consulting|quite|really))\b/i;
    const moveOn = isMoveOn(msg) && !WANTS_EDIT_RE.test(msg);

    // Step-specific handling (all use `moveOn` above except step 4 which
    // has its own connect/skip/done regexes below):
    //   • business_profile — requires both business_type + business_description
    //     already populated (recap-and-confirm flow); nudges the user if not.
    //   • qbo_link — bare "yes"/"no" is ambiguous re: migration intent, so
    //     defer to the LLM extractor / on-page Yes/No pill.
    //   • plaid_intent — dedicated CONNECT / SKIP / DONE regexes; affirmative
    //     launches Plaid Link, skip advances, done also advances.
    //   • veryfi/coa/ready — universally treated as move-on.
    // Affirmative-connect phrases specific to step 4 — user WANTS to link.
    // Anchored at start with a word boundary (not `$`) so compound answers
    // like "yes let's do it" or "sure, connect them" match cleanly.
    const PLAID_CONNECT_RE = /^(?:yes|yeah|yep|yup|sure|ok(?:ay)?|please|absolutely|of course|do it|let'?s (?:do (?:it|this)|connect|link|hook (?:it )?up)|connect(?: it| them| my (?:bank|accounts?))?|link(?: it| them| my (?:bank|accounts?))?|hook (?:it |them |me )?up|launch(?: it| plaid)?|open(?: plaid)?)\b/i;
    // Negative-skip phrases specific to step 4 — user does NOT want to link.
    const PLAID_SKIP_RE = /^(?:nope?|no(?:pe)?|not (?:now|yet|today)|later|do (?:it |this )?later|skip(?: (?:this|it|for now))?|come back|maybe later|no thanks)\b/i;
    // Post-link "we're done here" phrases — user finished Plaid and wants
    // to move on. Also fires when Plaid Link already ran successfully
    // (plaidAccts populated) and the user gives ANY move-on signal.
    const PLAID_DONE_RE = /\b(?:done|all done|already (?:done|linked|completed|connected)|completed|linked|connected|finished|we'?re good|we'?re (?:all )?set|all set|next(?: step)?|move on|let'?s (?:move on|go (?:to (?:the )?next|next)|continue|proceed)|good (?:to go)?)\b/i;

    if (script.extractStep === "plaid_intent") {
      const plaidAlreadyLinked = plaidAcctsRef.current.length > 0;
      // "done" / "next step" / "all set" always means advance — this fires
      // regardless of whether Plaid was linked (user may have completed the
      // popup, or hit skip in Plaid, or just wants out). Also catches
      // "let's go to the next step" which used to (incorrectly) re-launch
      // Plaid via the "let's go" alternative in CONNECT_RE.
      if (PLAID_DONE_RE.test(msg) || (moveOn && plaidAlreadyLinked)) {
        emitAction("onboarding-coach-greet", {
          message: plaidAlreadyLinked
            ? "Nice — bank connected. Moving on…"
            : "Got it — moving on…",
        });
        scheduleAdvance(() => nextRef.current());
        return;
      }
      if (PLAID_CONNECT_RE.test(msg)) {
        if (plaidAlreadyLinked) {
          // Don't re-launch — they already linked. Ask if they want to add
          // another account or move on.
          emitAction("onboarding-coach-greet", {
            message: "Bank's already connected. Want to link another, or should we move on to statement uploads?",
          });
          return;
        }
        emitAction("onboarding-coach-greet", {
          message: "Great — launching Plaid now. Sign in with your bank and I'll take it from there.",
        });
        // Small delay so the confirm bubble lands before Plaid's popup steals focus.
        setTimeout(() => emitAction("plaid-launch"), 600);
        return;
      }
      if (PLAID_SKIP_RE.test(msg)) {
        emitAction("onboarding-coach-greet", {
          message: "No problem — you can connect banks later from Settings. Moving on…",
        });
        scheduleAdvance(() => nextRef.current());
        return;
      }
      // Anything else on Plaid (questions like "what will this do?" or
      // ambiguous statements) — hand to the LLM coach-answer.
      await coachAnswerRef.current(msg, "plaid_intent");
      return;
    }

    if (script.extractStep === "plaid_credit_intent") {
      // Credit-card connect step. Same shape as plaid_intent above but
      // scoped to business cards — importantly, THIS was previously
      // missing a dedicated branch AND a backend `_COACH_STEP_SCHEMAS`
      // entry, so every reply on step 8+ silently 400'd from
      // /onboarding/extract-step and the chat went dark for the rest
      // of onboarding. Mirroring the bank block keeps parity.
      if (PLAID_DONE_RE.test(msg) || moveOn) {
        emitAction("onboarding-coach-greet", {
          message: "Got it — moving on…",
        });
        scheduleAdvance(() => nextRef.current());
        return;
      }
      if (PLAID_CONNECT_RE.test(msg)) {
        emitAction("onboarding-coach-greet", {
          message: "Great — launching Plaid now. Pick the credit-card account and I'll take it from there.",
        });
        setTimeout(() => emitAction("plaid-launch"), 600);
        return;
      }
      if (PLAID_SKIP_RE.test(msg)) {
        emitAction("onboarding-coach-greet", {
          message: "No problem — you can connect credit cards later from Settings. Moving on…",
        });
        scheduleAdvance(() => nextRef.current());
        return;
      }
      await coachAnswerRef.current(msg, "plaid_credit_intent");
      return;
    } else if (moveOn) {
      let doAdvance = false;
      let confirmText = "Moving on…";
      switch (script.extractStep) {
        case "business_profile": {
          const haveBoth =
            (currentAnswers.business_type || currentCompany?.business_type) &&
            (currentAnswers.business_description || currentCompany?.business_description);
          if (haveBoth) {
            doAdvance = true;
            confirmText = "Great — moving on…";
          } else {
            // Nothing on file yet — tell the user we need a bit more.
            emitAction("onboarding-coach-greet", {
              message: "Almost — I still need to know what kind of business this is and what it does. One sentence like \"we're an LLC doing IT security consulting for hospitals\" and we can move on.",
            });
            return;
          }
          break;
        }
        case "qbo_link":
          // Ambiguous — leave to the extractor / on-page pill.
          break;
        case "veryfi_intent":
          doAdvance = true;
          confirmText = "Got it — skipping statement uploads. Moving on…";
          break;
        case "coa_overrides":
          doAdvance = true;
          confirmText = "Great — moving on…";
          break;
        case "ready_confirm":
          emitAction("onboarding-coach-greet", { message: "Perfect — taking you in now." });
          scheduleAdvance(() => finishRef.current());
          return;
        default:
          break;
      }
      if (doAdvance) {
        emitAction("onboarding-coach-greet", { message: confirmText });
        scheduleAdvance(() => nextRef.current());
        return;
      }
    }

    try {
      const r = await api.post(
        `/companies/${currentId}/onboarding/extract-step`,
        { step: script.extractStep, message: msg },
      );
      const fields = r.data?.fields || {};
      if (!Object.keys(fields).length) {
        // No typed fields — the user is likely asking a question or making
        // an off-script comment. Route through the coach-answer endpoint
        // so they get a real answer instead of silence.
        await coachAnswerRef.current(msg, script.extractStep);
        return;
      }

      // Per-step side effects that MUST run before we render a confirm bubble.
      // Keeps the extractor server-side generic while letting each step
      // apply UI-level nudges (e.g. click a "yes/no" pill, toast the QBO
      // mock-link, or close out onboarding).
      const nextAnswers = { ...currentAnswers, ...fields };
      if (script.extractStep === "qbo_link" && (fields.qbo === "yes" || fields.qbo === "no")) {
        if (fields.qbo === "yes") {
          toast.success("QBO mock-linked. Data will sync in background.");
        }
      }
      setAnswers(nextAnswers);
      await persist(fields);

      const label = (k) => k.replace(/_/g, " ");
      const fmt = (v) =>
        Array.isArray(v) ? v.join(", ") : typeof v === "boolean" ? (v ? "yes" : "no") : String(v);
      const bits = Object.entries(fields)
        .map(([k, v]) => `**${label(k)}**: ${fmt(v)}`)
        .join(" · ");
      const ready = script.ready ? !!script.ready(fields, nextAnswers) : false;
      const confirmMsg = script.confirm
        ? script.confirm(bits, ready, fields)
        : ready
          ? `Got it — ${bits}. Moving on…`
          : `Got it — ${bits}.`;
      emitAction("onboarding-coach-greet", { message: confirmMsg });

      if (ready) {
        // 2.5s gives the confirmation time to visually register (and be
        // spoken aloud if voice output is on) before the page changes.
        // Step 6 (ready_confirm) is terminal — call finish() instead of next().
        setTimeout(() => {
          if (script.extractStep === "ready_confirm") {
            finishRef.current();
          } else {
            nextRef.current();
          }
        }, 2500);
      }
    } catch { /* non-fatal */ }
  });

  const persist = async (patch) => {
    await api.patch(`/companies/${currentId}/onboarding`, patch);
  };

  // Default onboarding is Simple — clients get through the essentials
  // (business profile, QBO, bank, statements, review) without the AI
  // Interview or AI-tailored CoA generation steps. Pros can flip to
  // "AI-guided" from the toggle at the top of the page when they want
  // the fully-personalized experience.
  const mode = answers.onboarding_mode === "guided" ? "guided" : "simple";
  // AI-only steps get skipped in "simple" mode.
  // - 5: AI Interview
  // - 6: AI-tailored Chart of Accounts
  const AI_ONLY_STEPS = new Set([5, 6]);
  const isInterviewStep = (s) => s === 5;   // kept for existing UI conditions
  const isAiOnlyStep = (s) => AI_ONLY_STEPS.has(s);

  const skipForward = (target) => {
    while (mode === "simple" && isAiOnlyStep(target) && target < STEPS.length) target += 1;
    return target;
  };
  const skipBackward = (target) => {
    while (mode === "simple" && isAiOnlyStep(target) && target > 0) target -= 1;
    return target;
  };

  const advance = async () => {
    const target = skipForward(step + 1);
    await persist({ step: target, answers });
    setStep(target);
  };

  const next = async () => {
    // When leaving the Responsibilities step (index 10), also persist
    // the assignments + payroll frequency to the company doc so the
    // To Do + Client Cockpit pages have data on first load.
    if (step === 10 && currentId) {
      try {
        await api.post(`/companies/${currentId}/responsibilities`, {
          assignments: answers.responsibilities || {},
          payroll_frequency: answers.payroll_frequency || null,
        });
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Couldn't save responsibilities — try again.");
        return;
      }
    }
    // Bank (7) & Credit card (8) steps: guard against skipping the
    // "Import & AI-categorize selected" click. If the user has any
    // account selected for this step that hasn't been imported yet,
    // pop a confirm modal instead of silently advancing.
    if (step === 7 || step === 8) {
      const isCredit = (a) => String(a.subtype || "").toLowerCase().includes("credit");
      const stepAccts = plaidAccts.filter(a => step === 8 ? isCredit(a) : !isCredit(a));
      const pending = stepAccts.filter(
        a => selectedPlaid.has(a.id) && !autoImportedRef.current.has(a.id)
      );
      if (pending.length > 0) {
        setPendingImportSkip({
          targetStep: skipForward(step + 1),
          kind: step === 8 ? "card" : "account",
          count: pending.length,
        });
        return;
      }
    }
    await advance();
  };
  const back = async () => {
    if (step <= 0) return;
    const target = Math.max(0, skipBackward(step - 1));
    await persist({ step: target });
    setStep(target);
  };
  const setMode = async (m) => {
    const nextAns = { ...answers, onboarding_mode: m };
    setAnswers(nextAns);
    // If flipping to simple while currently sitting on an AI-only step, hop
    // forward past every consecutive AI-only step.
    let nextStep = step;
    if (m === "simple" && isAiOnlyStep(step)) nextStep = skipForward(step);
    await persist({ answers: nextAns, step: nextStep });
    if (nextStep !== step) setStep(nextStep);
  };
  const finish = async () => {
    if (finishing) return;
    setFinishing(true);
    // Fire outbound invites for any Contact-step people that were
    // flagged with `send_invite: true`. We do this in the BACKGROUND
    // (Promise.allSettled, no await) so an outbound Resend round-trip
    // per contact doesn't freeze the "Next step" button for 5-10s.
    // Errors still surface as toasts; navigation happens after the
    // fast `persist` + `refresh` calls resolve.
    const contactsToInvite = Array.isArray(answers.contacts)
      ? answers.contacts.filter(c => c?.send_invite && (c?.email || "").trim())
      : [];
    const invitePromises = contactsToInvite.map((c) =>
      api.post(`/companies/${currentId}/invites`, {
        email: (c.email || "").trim(),
        name: (c.name || "").trim() || undefined,
        role: "editor",
      }).then(() => ({ ok: true, c }))
        .catch((e) => ({ ok: false, c, err: e }))
    );
    // Fire-and-forget: attach a handler that toasts failures but do NOT
    // await this before navigating. The user's UI moves on immediately.
    if (invitePromises.length > 0) {
      Promise.allSettled(invitePromises).then((results) => {
        const settled = results.map((r) => r.value || {});
        const sent = settled.filter((s) => s.ok).length;
        const failed = settled.filter((s) => s && !s.ok);
        failed.forEach((f) => {
          toast.error(
            `Couldn't invite ${f.c?.email}: ${f.err?.response?.data?.detail || f.err?.message || "email failed"}`,
          );
        });
        if (sent > 0) {
          toast.success(`${sent} invite${sent === 1 ? "" : "s"} sent.`);
        }
      });
    }
    // Optimistically clear the invite flag so returning here and hitting
    // Finish again doesn't double-send (backend re-invites also idempotent).
    const optimisticContacts = contactsToInvite.length
      ? (answers.contacts || []).map((c) =>
          c?.send_invite && (c?.email || "").trim()
            ? { ...c, send_invite: false, invited_at: new Date().toISOString() }
            : c,
        )
      : answers.contacts;
    try {
      await persist({
        complete: true,
        step: STEPS.length,
        answers: optimisticContacts !== answers.contacts
          ? { ...answers, contacts: optimisticContacts }
          : answers,
      });
      await refresh();
      toast.success(
        contactsToInvite.length
          ? "Onboarding complete! Sending invites in the background."
          : "Onboarding complete! Welcome to SmartBooks.",
      );
      nav("/welcome");
    } finally {
      setFinishing(false);
    }
  };
  // Keep the coach-handler refs pointed at the latest closures.
  useEffect(() => { nextRef.current = next; });
  useEffect(() => { finishRef.current = finish; });

  const loadInterview = async () => {
    setInterviewLoading(true);
    try {
      const r = await api.post(`/companies/${currentId}/onboarding/interview/questions`);
      setInterviewQs(r.data.questions || []);
    } catch {
      toast.error("AI couldn't generate interview questions. You can skip.");
    } finally {
      setInterviewLoading(false);
    }
  };

  const applyInterview = async () => {
    if (!interviewQs.length) return;
    setInterviewApplying(true);
    try {
      const answers = interviewQs.map(q => ({
        id: q.id, question: q.question,
        answer: interviewAns[q.id] ?? "",
      }));
      const r = await api.post(
        `/companies/${currentId}/onboarding/interview/synthesize`,
        { answers, apply: true },
      );
      setInterviewResult(r.data);
      toast.success(
        `Added ${r.data.inserted_accounts} account${r.data.inserted_accounts === 1 ? "" : "s"}`
        + ` and ${r.data.inserted_rules} rule${r.data.inserted_rules === 1 ? "" : "s"}`
        + (r.data.rules_applied_to_transactions
            ? ` · back-filled ${r.data.rules_applied_to_transactions} txns`
            : "")
      );
    } catch {
      toast.error("Failed to apply interview answers.");
    } finally {
      setInterviewApplying(false);
    }
  };

  const setInterviewAnswer = (id, val) =>
    setInterviewAns(p => ({ ...p, [id]: val }));


  const generateCoa = async () => {
    setPreviewing(true);
    setSuggestions([]);
    setAddedCount(0);
    try {
      const r = await api.post(`/companies/${currentId}/onboarding/coa/suggest`);
      const list = (r.data.suggestions || []).filter(s => !s.already_exists);
      setSuggestions(list);
      // Default: select all
      setCoaSelected(new Set(list.map(s => s.code)));
      if (!list.length) toast.info("No additional accounts needed — your CoA is already tailored.");
    } catch (err) {
      toast.error("AI could not generate suggestions. Try again.");
    } finally {
      setPreviewing(false);
    }
  };

  const applyCoa = async () => {
    if (coaSelected.size === 0) return;
    setAdding(true);
    try {
      const r = await api.post(`/companies/${currentId}/onboarding/generate-coa`, {
        codes: [...coaSelected],
      });
      setAddedCount(r.data.added || 0);
      // Remove inserted from the pending list
      const insertedCodes = new Set((r.data.inserted || []).map(s => s.code));
      setSuggestions(prev => prev.filter(s => !insertedCodes.has(s.code)));
      setCoaSelected(new Set());
      toast.success(`Added ${r.data.added} industry-specific account${r.data.added === 1 ? "" : "s"}`);
    } catch (err) {
      toast.error("Failed to add accounts.");
    } finally {
      setAdding(false);
    }
  };

  const toggleCoaSelected = (code) => setCoaSelected(prev => {
    const n = new Set(prev);
    n.has(code) ? n.delete(code) : n.add(code);
    return n;
  });
  const mockPlaid = async () => {
    setBusy(true);
    const r = await api.post(`/companies/${currentId}/onboarding/mock-plaid`);
    const accts = r.data.accounts || [];
    setBusy(false);
    // Reshape mock accounts into the same shape onPlaidLinked expects so we
    // reuse a single "auto-select + auto-import + coach announcement" path.
    onPlaidLinked(accts.map(a => ({
      account_id: a.id,
      name: a.name,
      mask: a.name.split("...")[1] || "",
      subtype: a.subtype,
      balance_current: a.balance,
      institution_name: a.institution || "Plaid Sandbox",
    })));
  };
  const onPlaidLinked = async (accounts) => {
    // Convert real Plaid accounts to the same UI shape as the mock
    const mapped = accounts.map(a => ({
      id: a.account_id, name: `${a.name || a.official_name} ...${a.mask || ""}`,
      institution: a.institution_name || "Plaid Sandbox", subtype: a.subtype || a.type,
      balance: a.balance_current || 0,
    }));
    // Append instead of replace — a user linking Wells Fargo AFTER Chase
    // should see both institutions' accounts on the page.
    setPlaidAccts(prev => {
      const known = new Set(prev.map(a => a.id));
      return [...prev, ...mapped.filter(a => !known.has(a.id))];
    });
    // Auto-check every returned account so the user's default is "pull
    // them all in" — but they can uncheck any they don't want before
    // clicking "Import & AI-categorize selected". No auto-import fires.
    const freshIds = mapped.map(a => a.id);
    setSelectedPlaid(prev => {
      const nextSet = new Set(prev);
      freshIds.forEach(id => nextSet.add(id));
      return nextSet;
    });
    if (!freshIds.length) return;
    const inst = accounts[0]?.institution_name || "your bank";
    const count = mapped.length;
    const already = plaidAccts.length; // rendered before setPlaidAccts flushes
    const msg = already
      ? `Nice — added ${count} more account${count === 1 ? "" : "s"} from ${inst}. I've pre-checked them. Uncheck any you'd rather leave out, then click "Import & AI-categorize selected" to pull transactions. Or say "link another" for a different bank.`
      : `Nice — I linked ${count} account${count === 1 ? "" : "s"} from ${inst}. I've pre-checked them all — uncheck any you'd rather skip, then click "Import & AI-categorize selected" to pull transactions and AI-categorize each. You can also say "link another" if you have another bank to add.`;
    emitAction("onboarding-coach-greet", { message: msg });
  };
  // Post-import "we're pulling data" popup. Fires the moment the user
  // clicks "Import & AI-categorize selected" so they know Plaid can take
  // up to ~10 min per institution to hand over all transactions. Set to
  // { kind: "bank" | "card" } while the AlertDialog is open.
  const [importStartedInfo, setImportStartedInfo] = useState(null);

  const importPlaid = async () => {
    const ids = [...selectedPlaid].filter(id => !autoImportedRef.current.has(id));
    if (!ids.length) {
      toast.info("Nothing new to import — every selected account is already downloaded.");
      return;
    }
    // Mark these IDs as in-progress BEFORE the await so re-clicks during
    // the download don't double-fire.
    ids.forEach(id => autoImportedRef.current.add(id));
    setBusy(true);
    // Open the "up to 10 min" notice immediately + echo it in the coach.
    // Step 8 = credit-card page, so the wording swaps "bank" → "credit institution".
    const kind = step === 8 ? "card" : "bank";
    setImportStartedInfo({ kind, count: ids.length });
    const noun = kind === "card" ? "card" : "account";
    const source = kind === "card" ? "credit institution" : "bank";
    emitAction("onboarding-coach-greet", {
      message: `Import started for ${ids.length} ${noun}${ids.length === 1 ? "" : "s"} — I'm pulling transactions and AI-categorizing each. This can take up to 10 minutes depending on your ${source}. Feel free to link another ${noun} or move to the next step in the meantime.`,
    });
    let importedCount = 0;
    let alreadyImported = 0;
    try {
      const r = await api.post(`/companies/${currentId}/onboarding/plaid/import`,
                                { account_ids: ids });
      importedCount = r.data.imported || 0;
      alreadyImported = r.data.already_imported || 0;
    } catch {
      const r = await api.post(`/companies/${currentId}/onboarding/import-plaid`, ids);
      importedCount = r.data.imported || 0;
      alreadyImported = r.data.already_imported || 0;
    } finally { setBusy(false); }
    setImported(v => ({ ...v, plaid: v.plaid + (importedCount || alreadyImported) }));
    const linkNoun = kind === "card" ? "card" : "bank";
    let doneMsg;
    if (alreadyImported > 0 && importedCount === 0) {
      doneMsg = `These accounts are already in your books (${alreadyImported} transactions previously imported). Continue with the flow, or say "link another" to add a ${linkNoun}.`;
    } else if (importedCount === 0) {
      doneMsg = `Nice — accounts connected. Transactions are downloading and being AI-categorized in the background right now (this can take up to 10 minutes for a fresh institution). Feel free to continue with the flow, or say "link another" if you have another ${linkNoun} to add.`;
    } else {
      doneMsg = `Done — pulled ${importedCount} transaction${importedCount === 1 ? "" : "s"} and AI-categorized each. Say "next" whenever you're ready to move on, or "link another" if you have more ${linkNoun}s to connect.`;
    }
    emitAction("onboarding-coach-greet", { message: doneMsg });
    toast.success(`AI categorized ${importedCount || alreadyImported} imported transactions`);
  };
  const mockVeryfi = async () => {
    setBusy(true);
    const r = await api.post(`/companies/${currentId}/onboarding/mock-veryfi`);
    setImported(v => ({ ...v, veryfi: r.data.imported }));
    setBusy(false);
    toast.success(`Veryfi OCR'd ${r.data.imported} statement lines`);
  };
  const uploadVeryfi = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const token = localStorage.getItem("axiom_token");
      const r = await fetch(`${BACKEND_URL}/api/companies/${currentId}/onboarding/veryfi/upload`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      if (!r.ok) throw new Error(await r.text());
      const d = await r.json();
      setImported(v => ({ ...v, veryfi: d.imported }));
      toast.success(`Veryfi OCR'd ${d.imported} lines. AI categorized each.`);
    } catch (e) {
      toast.error(`Veryfi upload failed: ${e.message}`);
    } finally { setUploading(false); }
  };

  const setAns = (k, v) => setAnswers({ ...answers, [k]: v });

  // Fixed-bottom footer alignment — see useColumnBox for details.
  const { columnRef, colBox } = useColumnBox([current?.id]);

  if (!current) return <div>Select a company.</div>;

  return (
    <div ref={columnRef} className="max-w-3xl mx-auto space-y-6 pb-24">
      <WelcomeModal open={welcomeOpen} onClose={closeWelcome} />
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-lg bg-indigo-100 flex items-center justify-center">
          <Sparkles className="text-indigo-600" size={20} />
        </div>
        <div className="flex-1">
          <h1 className="font-heading text-2xl font-bold">AI-assisted onboarding</h1>
          <p className="text-slate-500 text-sm">Getting {current.name} ready for the books.</p>
        </div>
        <div
          className="inline-flex rounded-md border border-slate-300 overflow-hidden text-xs"
          data-testid="onboarding-mode-toggle"
        >
          <button
            onClick={() => setMode("guided")}
            data-testid="onboarding-mode-guided"
            title="Full setup — AI interview + AI-tailored Chart of Accounts. More questions, deeper personalization."
            className={`px-3 py-1.5 flex items-center gap-1 ${
              mode === "guided" ? "bg-slate-900 text-white" : "bg-white text-slate-700 hover:bg-slate-50"
            }`}
          >
            <Sparkles size={11} /> Complex
          </button>
          <button
            onClick={() => setMode("simple")}
            data-testid="onboarding-mode-simple"
            title="Skip the AI interview + AI-tailored Chart of Accounts. Just business profile + QBO + bank + statements."
            className={`px-3 py-1.5 border-l border-slate-300 ${
              mode === "simple" ? "bg-slate-900 text-white" : "bg-white text-slate-700 hover:bg-slate-50"
            }`}
          >
            Simple
          </button>
        </div>
      </div>

      <div className="space-y-1.5" data-testid="onboarding-progress">
        {(() => {
          // Count only steps visible in the current mode (Simple hides
          // AI-only steps 5 & 6). "Step X of Y" reflects the user's
          // position among those visible steps, not the raw index.
          const visible = STEPS
            .map((s, i) => ({ s, i }))
            .filter(({ i }) => !(mode === "simple" && isAiOnlyStep(i)));
          const total = visible.length;
          const cursor = Math.max(0, visible.findIndex(({ i }) => i === step));
          const current1 = cursor >= 0 ? cursor + 1 : 1;
          const pct = total > 1 ? (cursor / (total - 1)) * 100 : 100;
          const jumpBack = async (targetIdx) => {
            // Backward-only: silently persist the target step so the
            // load-effect stays consistent on refresh, then update local
            // state. Answers auto-persist on each edit already, so
            // there's nothing to save here — mirrors what `back()` does.
            if (targetIdx >= step) return;
            await persist({ step: targetIdx });
            setStep(targetIdx);
          };
          return (
            <>
              <div className="flex items-baseline justify-between text-[11px] text-slate-500">
                <span>
                  <span className="font-medium text-slate-700">Step {current1}</span> of {total}
                </span>
                <span className="text-slate-600">{STEPS[step]}</span>
              </div>
              <div className="relative h-2 rounded-full bg-slate-200">
                <div
                  className="absolute inset-y-0 left-0 rounded-full bg-slate-900 transition-[width] duration-300 ease-out"
                  style={{ width: `${pct}%` }}
                  data-testid="onboarding-progress-bar"
                />
                <div className="absolute inset-0 flex justify-between items-center pointer-events-none">
                  {visible.map(({ s, i }, idx) => {
                    const isCurrent = i === step;
                    const isDone = idx < cursor;
                    const commonTip = `${idx + 1}. ${s}`;
                    if (isCurrent) {
                      return (
                        <div
                          key={i}
                          className="relative w-4 h-4 rounded-full bg-indigo-500 ring-2 ring-indigo-200 ring-offset-1 ring-offset-white shrink-0"
                          data-testid={`onboarding-progress-dot-${idx}`}
                          title={`${commonTip} · current`}
                        />
                      );
                    }
                    if (isDone) {
                      return (
                        <button
                          key={i}
                          type="button"
                          onClick={() => jumpBack(i)}
                          className="relative w-3 h-3 rounded-full bg-slate-900 hover:bg-slate-700 hover:scale-125 focus:scale-125 focus:outline-none focus:ring-2 focus:ring-slate-400 cursor-pointer transition-transform ring-2 ring-white shrink-0 pointer-events-auto"
                          data-testid={`onboarding-progress-dot-${idx}`}
                          title={`Jump back to ${commonTip}`}
                          aria-label={`Jump back to step ${idx + 1}: ${s}`}
                        />
                      );
                    }
                    // Upcoming — hollow, locked.
                    return (
                      <div
                        key={i}
                        className="relative w-3 h-3 rounded-full bg-white border-2 border-slate-300 shrink-0"
                        data-testid={`onboarding-progress-dot-${idx}`}
                        title={`${commonTip} · locked`}
                      />
                    );
                  })}
                </div>
              </div>
            </>
          );
        })()}
      </div>

      <div className="rounded-xl border bg-white p-6">
        {step === 0 && (
          <div className="space-y-5">
            <h2 className="font-heading text-xl font-semibold">Let's get you started with {current.name}</h2>

            <AiOnboardingPrefs answers={answers} setAnswers={setAnswers} persist={persist} />
          </div>
        )}

        {step === 1 && (
          <OnboardingContacts
            answers={answers}
            setAnswers={setAnswers}
            persist={persist}
            userEmail={user?.email || ""}
          />
        )}

        {step === 2 && (
          <div className="space-y-4">
            <div>
              <h2 className="font-heading text-xl font-semibold">Entity type</h2>
              <p className="text-sm text-slate-500 mt-0.5">
                We use this to create your chart of accounts.
              </p>
            </div>

            <div className="space-y-2" data-testid="onboarding-entity-type-picker">
              {BUSINESS_TYPES.map(t => {
                const selected = (answers.business_type || current.business_type) === t;
                return (
                  <button
                    key={t}
                    type="button"
                    role="radio"
                    aria-checked={selected}
                    data-testid={`onboarding-entity-type-${t.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}`}
                    onClick={() => {
                      const nextAns = { ...answers, business_type: t };
                      setAnswers(nextAns);
                      persist({ answers: nextAns });
                    }}
                    className={`w-full flex items-center gap-3 rounded-lg border px-4 py-3 text-sm text-left transition
                      ${selected
                        ? "border-indigo-500 bg-indigo-50 ring-2 ring-indigo-100"
                        : "border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50"}`}
                  >
                    <span
                      className={`w-4 h-4 rounded-full border-2 flex items-center justify-center flex-shrink-0
                        ${selected ? "border-indigo-500" : "border-slate-300"}`}
                    >
                      {selected && <span className="w-2 h-2 rounded-full bg-indigo-500" />}
                    </span>
                    <span className={selected ? "text-indigo-900 font-medium" : "text-slate-700"}>{t}</span>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="space-y-3">
            <h2 className="font-heading text-xl font-semibold">Tell us about {current.name}</h2>

            <div className="rounded-lg border border-indigo-200 bg-indigo-50/40 p-4">
              <label className="text-xs uppercase text-indigo-700 font-semibold tracking-wide">
                Industry
              </label>
              <p className="text-[11px] text-slate-600 mt-0.5 mb-3">
                We&apos;ll use this to create your chart of accounts. You can edit
                anything later.
              </p>
              <IndustrySelect
                companyId={currentId}
                label={current?.industry_label}
                slug={current?.industry_template}
                onChange={() => refresh?.()}
              />
            </div>
          </div>
        )}

        {step === 4 && (
          <div className="space-y-3">
            <h2 className="font-heading text-xl font-semibold">Do you already use QuickBooks Online?</h2>
            <p className="text-sm text-slate-500">We can link via QBO API and pull your existing chart of accounts and transactions.</p>
            <div className="flex gap-2">
              <button
                data-testid="onboarding-qbo-yes-btn"
                onClick={() => setAns("qbo", "yes")}
                className={`px-4 py-2 rounded-md border text-sm ${answers.qbo === "yes" ? "bg-slate-900 text-white" : ""}`}
              >
                Yes — link QuickBooks
              </button>
              <button
                data-testid="onboarding-qbo-no-btn"
                onClick={() => setAns("qbo", "no")}
                className={`px-4 py-2 rounded-md border text-sm ${answers.qbo === "no" ? "bg-slate-900 text-white" : ""}`}
              >
                No — next step
              </button>
            </div>

            {/* Inline connect wizard — appears the moment the user
                picks "Yes". Reuses the same endpoints as the standalone
                /connections/qbo page so nothing diverges. The return
                path is stamped on the oauth state row, so after the
                Intuit consent bounce the user lands back here on the
                onboarding step rather than the /connections/qbo page. */}
            {answers.qbo === "yes" && (
              <div
                data-testid="onboarding-qbo-inline-connect"
                className="mt-4 pt-4 border-t border-slate-100"
              >
                <InlineQboConnect
                  returnPath="/onboarding?step=4&qbo=connected"
                />
              </div>
            )}
          </div>
        )}

        {step === 5 && (
          <div className="space-y-3">
            <h2 className="font-heading text-xl font-semibold">Quick AI interview</h2>
            <p className="text-sm text-slate-500">
              5 targeted questions (under 30 seconds). Claude uses your answers to sharpen the
              chart of accounts and pre-configure bank-feed rules for your exact business.
            </p>

            {interviewQs.length === 0 && !interviewLoading && (
              <button
                onClick={loadInterview}
                data-testid="onboarding-interview-start"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-slate-900 text-white text-sm"
              >
                <Sparkles size={13} /> Start AI interview
              </button>
            )}
            {interviewLoading && (
              <div className="text-sm text-slate-500 inline-flex items-center gap-2">
                <Loader2 size={14} className="animate-spin" />
                Preparing questions tailored to your business…
              </div>
            )}

            {interviewQs.length > 0 && !interviewResult && (
              <div className="space-y-3 mt-2">
                {interviewQs.map((q, idx) => (
                  <div
                    key={q.id}
                    data-testid={`interview-q-${q.id}`}
                    className="rounded-md border bg-white p-3"
                  >
                    <div className="text-sm font-medium mb-1">
                      <span className="text-slate-400 mr-1">{idx + 1}.</span>{q.question}
                    </div>
                    {q.why && <div className="text-[11px] text-slate-500 mb-2">{q.why}</div>}
                    {q.answer_type === "yes_no" ? (
                      <div className="flex gap-2">
                        {["Yes", "No"].map(v => (
                          <button
                            key={v}
                            onClick={() => setInterviewAnswer(q.id, v)}
                            data-testid={`interview-a-${q.id}-${v.toLowerCase()}`}
                            className={`px-3 py-1 rounded-md border text-xs ${
                              interviewAns[q.id] === v
                                ? "bg-slate-900 text-white border-slate-900"
                                : "bg-white hover:bg-slate-50"
                            }`}
                          >
                            {v}
                          </button>
                        ))}
                      </div>
                    ) : q.answer_type === "multi_choice" ? (
                      <div className="flex flex-wrap gap-2">
                        {(q.options || []).map(opt => (
                          <button
                            key={opt}
                            onClick={() => setInterviewAnswer(q.id, opt)}
                            data-testid={`interview-a-${q.id}-${opt.substring(0,12)}`}
                            className={`px-3 py-1 rounded-md border text-xs ${
                              interviewAns[q.id] === opt
                                ? "bg-slate-900 text-white border-slate-900"
                                : "bg-white hover:bg-slate-50"
                            }`}
                          >
                            {opt}
                          </button>
                        ))}
                      </div>
                    ) : (
                      <input
                        placeholder="Type your answer…"
                        value={interviewAns[q.id] || ""}
                        onChange={(e) => setInterviewAnswer(q.id, e.target.value)}
                        data-testid={`interview-a-${q.id}-text`}
                        className="w-full border rounded px-2 py-1.5 text-sm"
                      />
                    )}
                  </div>
                ))}
                <button
                  onClick={applyInterview}
                  disabled={interviewApplying
                    || interviewQs.some(q => !interviewAns[q.id])}
                  data-testid="onboarding-interview-apply"
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-emerald-600 text-white text-sm disabled:opacity-50"
                >
                  {interviewApplying && <Loader2 size={14} className="animate-spin" />}
                  Apply AI recommendations
                </button>
              </div>
            )}

            {interviewResult && (
              <div className="mt-3 space-y-3">
                <div className="rounded-md bg-emerald-50 border border-emerald-200 px-3 py-2 text-sm">
                  ✓ Added <b>{interviewResult.inserted_accounts}</b> account{interviewResult.inserted_accounts === 1 ? "" : "s"}
                  {" "}and <b>{interviewResult.inserted_rules}</b> rule{interviewResult.inserted_rules === 1 ? "" : "s"}.
                  {interviewResult.rules_applied_to_transactions > 0 && (
                    <> Back-filled <b>{interviewResult.rules_applied_to_transactions}</b> un-reviewed transaction{interviewResult.rules_applied_to_transactions === 1 ? "" : "s"}.</>
                  )}
                </div>
                {interviewResult.accounts?.length > 0 && (
                  <div className="rounded-md border bg-white">
                    <div className="px-3 py-2 border-b text-xs uppercase tracking-wide text-slate-500 bg-slate-50">
                      New accounts
                    </div>
                    <div className="divide-y max-h-56 overflow-y-auto">
                      {interviewResult.accounts.map(a => (
                        <div key={a.code} className="px-3 py-2 text-sm">
                          <span className="font-mono-num text-slate-500 tabular-nums mr-2">{a.code}</span>
                          <span className="font-medium">{a.name}</span>
                          <span className="text-[10px] uppercase px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 ml-2">{a.type}</span>
                          {a.rationale && <div className="text-[11px] text-slate-500 mt-0.5">{a.rationale}</div>}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {interviewResult.rules?.length > 0 && (
                  <div className="rounded-md border bg-white">
                    <div className="px-3 py-2 border-b text-xs uppercase tracking-wide text-slate-500 bg-slate-50">
                      Seed rules
                    </div>
                    <div className="divide-y max-h-56 overflow-y-auto">
                      {interviewResult.rules.map((r, i) => (
                        <div key={i} className="px-3 py-2 text-sm">
                          When merchant contains <b>{r.merchant}</b> → <b className="font-mono-num tabular-nums">{r.account_code}</b> {r.account_name}
                          {r.why && <div className="text-[11px] text-slate-500 mt-0.5">{r.why}</div>}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}


        {step === 6 && (
          <div className="space-y-3">
            <h2 className="font-heading text-xl font-semibold">AI-tailored Chart of Accounts</h2>
            <p className="text-sm text-slate-500">
              We seed a GAAP baseline. Claude Sonnet reads your business type + description and
              proposes 15-25 industry-specific accounts you can review before adding.
            </p>
            <div className="flex flex-wrap gap-2">
              <button
                data-testid={TID.onboardingCoaGenerate}
                onClick={generateCoa}
                disabled={previewing || adding}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-slate-900 text-white text-sm disabled:opacity-50"
              >
                {previewing && <Loader2 size={13} className="animate-spin" />}
                {previewing ? "Analyzing…" : suggestions.length ? "Re-analyze" : "Suggest tailored accounts"}
              </button>
              {suggestions.length > 0 && (
                <button
                  onClick={applyCoa}
                  disabled={adding || coaSelected.size === 0}
                  data-testid="onboarding-coa-apply"
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-emerald-600 text-white text-sm disabled:opacity-50"
                >
                  {adding && <Loader2 size={13} className="animate-spin" />}
                  Add {coaSelected.size} selected
                </button>
              )}
            </div>

            {addedCount > 0 && (
              <div className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-md px-3 py-2">
                ✓ Added {addedCount} account{addedCount === 1 ? "" : "s"} to your chart of accounts.
              </div>
            )}

            {suggestions.length > 0 && (
              <div className="mt-2 rounded-md border bg-white">
                <div className="flex items-center gap-2 px-3 py-2 border-b bg-slate-50">
                  <input
                    type="checkbox"
                    checked={coaSelected.size === suggestions.length && suggestions.length > 0}
                    onChange={() => setCoaSelected(
                      coaSelected.size === suggestions.length
                        ? new Set()
                        : new Set(suggestions.map(s => s.code))
                    )}
                    data-testid="onboarding-coa-select-all"
                  />
                  <div className="text-xs text-slate-600">
                    <b>{coaSelected.size}</b> of {suggestions.length} selected
                  </div>
                </div>
                <div className="divide-y max-h-[360px] overflow-y-auto">
                  {suggestions.map(s => (
                    <label
                      key={s.code}
                      data-testid={`onboarding-coa-option-${s.code}`}
                      className={`flex items-start gap-3 px-3 py-2 cursor-pointer ${
                        coaSelected.has(s.code) ? "bg-emerald-50/40" : "hover:bg-slate-50"
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={coaSelected.has(s.code)}
                        onChange={() => toggleCoaSelected(s.code)}
                        className="mt-1"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm flex items-baseline gap-2 flex-wrap">
                          <span className="font-mono-num text-slate-500 tabular-nums">{s.code}</span>
                          <span className="font-medium">{s.name}</span>
                          <span className="text-[10px] uppercase px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">
                            {s.type}
                          </span>
                        </div>
                        {s.rationale && (
                          <div className="text-[11px] text-slate-500 mt-0.5">{s.rationale}</div>
                        )}
                      </div>
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {step === 7 && (
          <div className="space-y-3">
            <h2 className="font-heading text-xl font-semibold">Connect your bank via Plaid</h2>
            <p className="text-sm text-slate-500">
              Select which accounts belong to this company. We log the balance with every transaction so we can auto-reconcile later.
            </p>
            {(() => {
              // Bank step shows depository accounts only. Credit-card subtypes
              // get their own dedicated step next so users can pick which
              // cards belong to the business separately from checking/savings.
              const isCredit = (a) => String(a.subtype || "").toLowerCase().includes("credit");
              const bankAccts = plaidAccts.filter(a => !isCredit(a));
              return !bankAccts.length ? (
              <div className="flex gap-2 flex-wrap">
                <PlaidLinkButton companyId={currentId} companyName={current?.name} companyOwner={current?.owner_email} onSuccess={onPlaidLinked} disabled={busy} />
              </div>
            ) : (
              <div className="space-y-4">
                {(() => {
                  // Group accounts by institution for a nicer scannable list:
                  //   [Chase Business] logo · 2 accounts
                  //     [x] Adv Plus Banking …6084    $3,986.57
                  //     [x] Everyday Checking …0036    $319.19
                  //   [Wells Fargo] logo · 1 account
                  //     [x] Wells Business …9917       $112.88
                  const groups = new Map();
                  for (const a of bankAccts) {
                    const key = a.institution || "Other";
                    if (!groups.has(key)) groups.set(key, []);
                    groups.get(key).push(a);
                  }
                  return [...groups.entries()].map(([inst, accts]) => {
                    const logo = institutionLogoUrl(inst);
                    // Bulk-toggle helper for the whole institution.
                    const allChecked = accts.every(a => selectedPlaid.has(a.id));
                    const someChecked = accts.some(a => selectedPlaid.has(a.id));
                    const toggleAll = () => {
                      const s = new Set(selectedPlaid);
                      if (allChecked) accts.forEach(a => s.delete(a.id));
                      else accts.forEach(a => s.add(a.id));
                      setSelectedPlaid(s);
                    };
                    return (
                      <div key={inst} className="border rounded-md overflow-hidden">
                        <div className="flex items-center gap-3 px-3 py-2 bg-slate-50 border-b">
                          {logo ? (
                            <img src={logo} alt=""
                                 onError={(e) => { e.currentTarget.style.display = 'none'; }}
                                 className="w-6 h-6 rounded-full bg-white ring-1 ring-slate-200 object-contain shrink-0" />
                          ) : (
                            <div className="w-6 h-6 rounded-full bg-slate-200 text-slate-600 text-[10px] font-semibold flex items-center justify-center shrink-0">
                              {(inst[0] || "?").toUpperCase()}
                            </div>
                          )}
                          <div className="flex-1">
                            <div className="text-sm font-semibold">{inst}</div>
                            <div className="text-[11px] text-slate-500">{accts.length} account{accts.length === 1 ? "" : "s"} connected</div>
                          </div>
                          <button type="button" onClick={toggleAll}
                                  className="text-[11px] px-2 py-1 rounded border bg-white text-slate-600 hover:bg-slate-100">
                            {allChecked ? "Uncheck all" : someChecked ? "Check all" : "Check all"}
                          </button>
                        </div>
                        <div className="divide-y">
                          {accts.map(a => (
                            <label key={a.id} className="flex items-center gap-3 px-3 py-2.5 hover:bg-slate-50/60">
                              <input type="checkbox" checked={selectedPlaid.has(a.id)}
                                     onChange={(e) => {
                                       const s = new Set(selectedPlaid);
                                       e.target.checked ? s.add(a.id) : s.delete(a.id);
                                       setSelectedPlaid(s);
                                     }} />
                              <div className="flex-1">
                                <div className="font-medium text-sm">{a.name}</div>
                                <div className="text-xs text-slate-500 capitalize">{a.subtype}</div>
                              </div>
                              <div className="font-mono-num text-sm">${Number(a.balance || 0).toLocaleString()}</div>
                            </label>
                          ))}
                        </div>
                      </div>
                    );
                  });
                })()}
                {(() => {
                  // Compute which selected accounts still need to be
                  // auto-imported. Once every selected account is in the
                  // in-progress or completed set, dim the manual Import
                  // button so the user can't accidentally re-trigger it.
                  const pendingSelected = [...selectedPlaid].filter(id => !autoImportedRef.current.has(id));
                  const allDone = selectedPlaid.size > 0 && pendingSelected.length === 0;
                  return (
                    <div className="flex flex-wrap items-center gap-3">
                      <button onClick={importPlaid}
                              disabled={!pendingSelected.length || busy || allDone}
                              title={allDone ? "All selected accounts are already downloading / imported." : ""}
                              className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-emerald-600 text-white text-sm disabled:opacity-40 disabled:cursor-not-allowed">
                        {busy && <Loader2 size={13} className="animate-spin" />} Import & AI-categorize selected
                      </button>
                      {/* Link another institution — Chase + Wells Fargo + Amex, one page. */}
                      <PlaidLinkButton
                        companyId={currentId}
                        companyName={current?.name}
                        companyOwner={current?.owner_email}
                        onSuccess={onPlaidLinked}
                        disabled={busy}
                        label="Link another bank"
                      />
                      {allDone && (
                        <span className="text-xs text-slate-500">
                          Downloading & AI-categorizing in the background — feel free to continue.
                        </span>
                      )}
                    </div>
                  );
                })()}
                {imported.plaid > 0 && (
                  <div className="text-xs text-emerald-700">✓ Imported {imported.plaid} transactions. AI categorized each per GAAP.</div>
                )}
              </div>
            );
            })()}
          </div>
        )}

        {step === 8 && (
          <div className="space-y-3">
            <h2 className="font-heading text-xl font-semibold">Connect your credit cards via Plaid</h2>
            <p className="text-sm text-slate-500">
              Select which business credit cards belong to this company. We pull the balance and every charge, then AI-categorize each one so month-end reconciliation is automatic.
            </p>
            {(() => {
              // Credit-card step mirrors the bank step but filters to cards
              // only. Plaid returns credit accounts with either
              // `type === "credit"` or `subtype === "credit card"` — we
              // stored either into `subtype` at link time, so a single
              // substring check catches both.
              const isCredit = (a) => String(a.subtype || "").toLowerCase().includes("credit");
              const cardAccts = plaidAccts.filter(isCredit);
              return !cardAccts.length ? (
                <div className="flex gap-2 flex-wrap">
                  <PlaidLinkButton
                    companyId={currentId}
                    companyName={current?.name}
                    companyOwner={current?.owner_email}
                    onSuccess={onPlaidLinked}
                    disabled={busy}
                    label="Link a credit card"
                  />
                </div>
              ) : (
                <div className="space-y-4">
                  {(() => {
                    const groups = new Map();
                    for (const a of cardAccts) {
                      const key = a.institution || "Other";
                      if (!groups.has(key)) groups.set(key, []);
                      groups.get(key).push(a);
                    }
                    return [...groups.entries()].map(([inst, accts]) => {
                      const logo = institutionLogoUrl(inst);
                      const allChecked = accts.every(a => selectedPlaid.has(a.id));
                      const someChecked = accts.some(a => selectedPlaid.has(a.id));
                      const toggleAll = () => {
                        const s = new Set(selectedPlaid);
                        if (allChecked) accts.forEach(a => s.delete(a.id));
                        else accts.forEach(a => s.add(a.id));
                        setSelectedPlaid(s);
                      };
                      return (
                        <div key={inst} className="border rounded-md overflow-hidden">
                          <div className="flex items-center gap-3 px-3 py-2 bg-slate-50 border-b">
                            {logo ? (
                              <img src={logo} alt=""
                                   onError={(e) => { e.currentTarget.style.display = 'none'; }}
                                   className="w-6 h-6 rounded-full bg-white ring-1 ring-slate-200 object-contain shrink-0" />
                            ) : (
                              <div className="w-6 h-6 rounded-full bg-slate-200 text-slate-600 text-[10px] font-semibold flex items-center justify-center shrink-0">
                                {(inst[0] || "?").toUpperCase()}
                              </div>
                            )}
                            <div className="flex-1">
                              <div className="text-sm font-semibold">{inst}</div>
                              <div className="text-[11px] text-slate-500">{accts.length} card{accts.length === 1 ? "" : "s"} connected</div>
                            </div>
                            <button type="button" onClick={toggleAll}
                                    className="text-[11px] px-2 py-1 rounded border bg-white text-slate-600 hover:bg-slate-100">
                              {allChecked ? "Uncheck all" : "Check all"}
                            </button>
                          </div>
                          <div className="divide-y">
                            {accts.map(a => (
                              <label key={a.id} className="flex items-center gap-3 px-3 py-2.5 hover:bg-slate-50/60">
                                <input type="checkbox" checked={selectedPlaid.has(a.id)}
                                       onChange={(e) => {
                                         const s = new Set(selectedPlaid);
                                         e.target.checked ? s.add(a.id) : s.delete(a.id);
                                         setSelectedPlaid(s);
                                       }} />
                                <div className="flex-1">
                                  <div className="font-medium text-sm">{a.name}</div>
                                  <div className="text-xs text-slate-500 capitalize">{a.subtype}</div>
                                </div>
                                <div className="font-mono-num text-sm">${Number(a.balance || 0).toLocaleString()}</div>
                              </label>
                            ))}
                          </div>
                        </div>
                      );
                    });
                  })()}
                  {(() => {
                    const pendingSelected = [...selectedPlaid].filter(id => !autoImportedRef.current.has(id) && cardAccts.some(a => a.id === id));
                    const selectedCards = [...selectedPlaid].filter(id => cardAccts.some(a => a.id === id));
                    const allDone = selectedCards.length > 0 && pendingSelected.length === 0;
                    return (
                      <div className="flex flex-wrap items-center gap-3">
                        <button onClick={importPlaid}
                                disabled={!pendingSelected.length || busy || allDone}
                                title={allDone ? "All selected cards are already downloading / imported." : ""}
                                className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-emerald-600 text-white text-sm disabled:opacity-40 disabled:cursor-not-allowed">
                          {busy && <Loader2 size={13} className="animate-spin" />} Import & AI-categorize selected
                        </button>
                        <PlaidLinkButton
                          companyId={currentId}
                          companyName={current?.name}
                          companyOwner={current?.owner_email}
                          onSuccess={onPlaidLinked}
                          disabled={busy}
                          label="Link another card"
                        />
                        {allDone && (
                          <span className="text-xs text-slate-500">
                            Downloading & AI-categorizing in the background — feel free to continue.
                          </span>
                        )}
                      </div>
                    );
                  })()}
                </div>
              );
            })()}
          </div>
        )}

        {step === 9 && (
          <div className="space-y-3">
            <h2 className="font-heading text-xl font-semibold">Upload statements Plaid couldn't reach</h2>
            <p className="text-sm text-slate-500">
              Our AI pulls transactions off PDFs and images and categorizes each per GAAP.
              Old paper statements, credit-union PDFs, receipts — drop them here.
            </p>
            {/* Reuse the same drop-zone / imports table experience from the
                Connections › Statements tab so onboarding matches the rest of
                the app. `bare` skips the tab's own outer card since we're
                already inside the onboarding step card. */}
            <StatementsTab companyId={currentId} bare />
            {imported.veryfi > 0 && (
              <div className="text-xs text-emerald-700">✓ Imported {imported.veryfi} lines. AI categorized each per GAAP.</div>
            )}
          </div>
        )}

        {step === 10 && (
          <div className="space-y-3">
            <h2 className="font-heading text-xl font-semibold">Who does what each month?</h2>
            <p className="text-sm text-slate-500">
              For each recurring activity, tell me who owns it. "Both" puts the item on
              both the accountant's Client Cockpit and the client's To Do page. You can
              edit these any time from either page.
            </p>
            <ResponsibilitiesChecklist
              assignments={answers.responsibilities || {}}
              payrollFrequency={answers.payroll_frequency || null}
              onAssignmentChange={(key, val) => {
                const next = { ...(answers.responsibilities || {}), [key]: val };
                setAnswers(a => ({
                  ...a,
                  responsibilities: next,
                  // Drop frequency if payroll got unchecked or marked N/A.
                  ...(key === "issuing_payroll" && (!val || val === "n/a") ? { payroll_frequency: null } : {}),
                }));
              }}
              onBulkAssign={(val, keys) => {
                setAnswers(a => {
                  const next = { ...(a.responsibilities || {}) };
                  keys.forEach(k => { next[k] = val; });
                  return {
                    ...a,
                    responsibilities: next,
                    // Same guard as the single-row path: clearing payroll
                    // (val === null) also clears the frequency.
                    ...(!val ? { payroll_frequency: null } : {}),
                  };
                });
              }}
              onFrequencyChange={(freq) => setAnswers(a => ({ ...a, payroll_frequency: freq }))}
            />
          </div>
        )}

        {step === 11 && (
          <div className="space-y-3">
            <h2 className="font-heading text-xl font-semibold">Company setup is all done.</h2>
            <p className="text-sm text-slate-500">
              I'm working in the background now, handling the accounting drudgery — importing transactions,
              AI-categorizing each one, and flagging anything I'm not sure about. Give me a couple of minutes
              to finish. Go ahead and hit <span className="font-medium text-slate-700">"Next step"</span> whenever
              you're ready — I'll see you there.
            </p>
          </div>
        )}
      </div>

      {/* Fixed viewport-bottom footer — stays glued to the bottom of the
          page while remaining horizontally centered under the info card.
          `position: fixed` combined with a measured `left` + `width`
          (mirrored from `columnRef` above via ResizeObserver) means the
          footer tracks the main content column even when the left
          sidebar or right AI-chat panel is toggled. */}
      <div
        style={{
          position: "fixed",
          bottom: 16,
          left: colBox.left,
          width: colBox.width,
          visibility: colBox.ready ? "visible" : "hidden",
        }}
        className="z-30 flex items-center justify-center gap-3 pointer-events-none"
        data-testid="onboarding-sticky-footer"
      >
        <div className="flex items-center justify-center gap-3 pointer-events-auto">
          <button
            data-testid={TID.onboardingBack}
            disabled={step === 0}
            onClick={back}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-white border border-slate-200 shadow-sm text-sm text-slate-600 hover:text-slate-900 hover:border-slate-300 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <ArrowLeft size={14} /> Back
          </button>
          {step < STEPS.length - 1 ? (
            <button
              data-testid={TID.onboardingNext}
              onClick={next}
              className="inline-flex items-center gap-1.5 px-5 py-2 rounded-full bg-slate-900 text-white text-sm shadow-md hover:bg-slate-800"
            >
              Next <ChevronRight size={14} />
            </button>
          ) : (
            <button
              data-testid={TID.onboardingComplete}
              onClick={finish}
              disabled={finishing}
              className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-emerald-600 text-white text-sm shadow-md hover:bg-emerald-700 disabled:opacity-60 disabled:cursor-wait"
            >
              {finishing ? (
                <>Finishing… <Loader2 size={14} className="animate-spin" /></>
              ) : (
                <>Next step <ChevronRight size={14} /></>
              )}
            </button>
          )}
        </div>
      </div>

      <AlertDialog
        open={!!pendingImportSkip}
        onOpenChange={(o) => { if (!o) setPendingImportSkip(null); }}
      >
        <AlertDialogContent data-testid="onboarding-import-gate-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>
              Import {pendingImportSkip?.count} {pendingImportSkip?.kind}
              {pendingImportSkip && pendingImportSkip.count === 1 ? "" : "s"} first?
            </AlertDialogTitle>
            <AlertDialogDescription>
              You've selected {pendingImportSkip?.count}{" "}
              {pendingImportSkip?.kind}
              {pendingImportSkip && pendingImportSkip.count === 1 ? "" : "s"}{" "}
              but haven't imported {pendingImportSkip && pendingImportSkip.count === 1 ? "it" : "them"} yet.
              Click <span className="font-semibold">"Import &amp; AI-categorize selected"</span> below to pull
              transactions and AI-categorize each. Otherwise you can skip
              and finish the import later from Settings.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              data-testid="onboarding-import-gate-cancel"
              onClick={() => setPendingImportSkip(null)}
            >
              Go back and import
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="onboarding-import-gate-skip"
              onClick={async () => {
                setPendingImportSkip(null);
                await advance();
              }}
              className="bg-slate-900 text-white hover:bg-slate-800"
            >
              Skip import and continue
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={!!importStartedInfo}
        onOpenChange={(o) => { if (!o) setImportStartedInfo(null); }}
      >
        <AlertDialogContent data-testid="onboarding-import-started-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>
              Import started — hang tight
            </AlertDialogTitle>
            <AlertDialogDescription>
              The process has started for {importStartedInfo?.count}{" "}
              {importStartedInfo?.kind === "card" ? "credit card" : "account"}
              {importStartedInfo && importStartedInfo.count === 1 ? "" : "s"}, but it might take
              up to 10 minutes to receive all of the transactions depending on your{" "}
              {importStartedInfo?.kind === "card" ? "credit institution" : "bank"}.
              You can keep going in the meantime — I'll AI-categorize each
              transaction as they land.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              data-testid="onboarding-import-started-add-more"
              onClick={() => setImportStartedInfo(null)}
            >
              Add additional {importStartedInfo?.kind === "card" ? "card" : "account"}
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="onboarding-import-started-next"
              onClick={async () => {
                setImportStartedInfo(null);
                await advance();
              }}
              className="bg-slate-900 text-white hover:bg-slate-800"
            >
              Move to the next step
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
